import * as React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Checkbox,
  CheckboxVisibility,
  DatePicker,
  DefaultButton,
  defaultDatePickerStrings,
  Dropdown,
  IColumn,
  Link,
  Modal,
  PrimaryButton,
  SearchBox,
  addDays,
  MessageBarType,
  MessageBar,
} from '@fluentui/react';
import { TooltipHost } from '@fluentui/react/lib/Tooltip';
import { IconButton } from '@fluentui/react/lib/Button';
import { Selection } from '@fluentui/react/lib/DetailsList';
import * as Api from '../../../api/src';
import ModalHeader from '../../../control/modalHeader';
import { NoDataFound } from '../../../control/noDataFound';
import { commonfn } from '../../../common/commonfn';
import Moment from 'moment';
import { AddProductToMatrixCommand } from '../../../api/src';
import { TeuDetailsList } from '../../../control/detailList';

export interface ModalAddProductsProps {
  IsOpen: boolean;
  onCloseModal: () => void;
  onSaveClick?: () => void;
  ProductTypeId: number;
  Year: number;
}
const MatrixApi = new Api.MatrixsApi();
const BrandsApi = new Api.BrandsApi();
const CategoriesApi = new Api.CategoriesApi();
const VendorsApi = new Api.VendorsApi();
let _selection: Selection | undefined = new Selection();
let _selectionModules: Selection | undefined = new Selection();
const defaultFilter = {
  Year: Moment().year(),
  OrderBy: [{ ColumnName: 'Name' }],
  PageSize: 0,
  PageNumber: 1,
  ProductTypeId: 0,
  BrandId: 0,
  CategoryId: 0,
  VendorId: 0,
  IsNewProductonly: true,
  MatrixYear: Moment().year(),
} as Api.GetPagedProductsToAddMatrixQuery;

const ModalAddProducts = ({ IsOpen, onCloseModal, onSaveClick, ProductTypeId, Year }: ModalAddProductsProps) => {
  const { t } = useTranslation();
  const [isShow, SetIsShow] = React.useState(false);
  const [showProductError, SetShowProductError] = React.useState(false);
  const [showModuleError, SetShowModuleError] = React.useState(false);
  const [isModule, SetIsModule] = React.useState(false);
  const [listData, setListData] = React.useState<Api.Product[]>();
  const [listModules, setListModules] = React.useState<(Api.Module & { DeadLine: string; datePick: boolean })[]>();
  const [listYears, setListYears] = React.useState<any[]>([]);
  const [listBrand, setListBrand] = React.useState<any[]>([]);
  const [listVendors, setListVendors] = React.useState<any[]>([]);
  const [listCategories, setListCategories] = React.useState<any[]>([]);
  const [FilterQuery, setFilterQuery] = React.useState<Api.GetPagedProductsToAddMatrixQuery>({
    ...defaultFilter,
    ProductTypeId: ProductTypeId,
    Year: Year,
    MatrixYear: Year,
  });
  const [prevState, setPrevState] = React.useState<Api.GetPagedProductsToAddMatrixQuery>({
    ...defaultFilter,
    ProductTypeId: ProductTypeId,
    Year: Year,
    MatrixYear: Year,
  });
  const _columns = [
    {
      key: 'Model',
      name: t('Model'),
      fieldName: 'Name',
      className: 'flex-center',
      isResizable: true,
      minWidth: 200,
      onRender: (item: Api.Product) => {
        return item.Name;
      },
    },
    {
      key: 'Category',
      name: t('Category'),
      className: 'flex-center',
      minWidth: 200,
      isResizable: true,
      onRender: (item: Api.Product) => {
        return item.Category?.Name;
      },
    },
    {
      key: 'Brand',
      name: t('Brand'),
      className: 'flex-center',
      minWidth: 200,
      isResizable: true,
      onRender: (item: Api.Product) => {
        return item.Brand?.Name;
      },
    },
    {
      key: 'Year',
      name: t('Year'),
      className: 'flex-center',
      minWidth: 80,
      onRender: (item: Api.Product) => {
        return item.Year;
      },
    },
  ] as IColumn[];
  const _columnsModule = [
    {
      key: 'Module',
      name: t('ModuleName'),
      fieldName: 'Name',
      className: 'flex-center',
      isResizable: true,
      minWidth: 250,
      onRender: (item: Api.Module) => {
        return item.Name;
      },
    },
    {
      key: 'Component',
      name: t('Component'),
      className: 'flex-center',
      minWidth: 150,
      onRender: (item: Api.Module) => {
        return item.Component?.Name ?? '-';
      },
    },
    {
      key: 'Deadline',
      name: t('Deadline'),
      className: 'flex-center',
      minWidth: 150,
      onRender: (item: Api.Module) => {
        return (
          <DatePicker
            //firstDayOfWeek={DayOfWeek.Sunday}
            showWeekNumbers={true}
            firstWeekOfYear={1}
            showMonthPickerAsOverlay={true}
            minDate={addDays(new Date(), 1)}
            //isRequired={!!TestDateErrorMessage}
            value={item.DefaultDeadlineDay ? Moment().add(item.DefaultDeadlineDay, 'd').toDate() : undefined}
            placeholder="dd/mm/yyyy"
            formatDate={(date?: Date): string => {
              return Moment(date).format('DD/MM/yyyy');
            }}
            onSelectDate={(date: Date | null | undefined) => {
              listModules?.forEach((element) => {
                if (element.Id === item.Id) {
                  element.DefaultDeadlineDay = Moment(date).diff(Moment().startOf('d'), 'd');
                  element.datePick = true;
                }
              });
              setListModules(listModules);
            }}
            strings={defaultDatePickerStrings}
          />
        );
      },
    },
    {
      key: 'Action',
      name: '',
      className: 'flex-center',
      onRender: (item, index) => {
        if (item.DefaultDeadlineDay)
          return (
            <Link
              onClick={() => {
                if (item.DefaultDeadlineDay) {
                  listModules?.forEach((element) => {
                    element.DefaultDeadlineDay = item.DefaultDeadlineDay;
                    element.datePick = true;
                  });
                  setListModules([...(listModules || [])]);
                }
              }}
            >
              {t('ApplyForAll')}
            </Link>
          );
      },
    },
  ] as IColumn[];
  React.useEffect(() => {
    _selection = new Selection();
    _selectionModules = new Selection();
    SetIsShow(IsOpen);
    if (IsOpen) {
      getListData();
      getAllBrands();
      getAllCategories();
      getAllVendors();
      getModules();
    } else {
      setFilterQuery({ ...defaultFilter, ProductTypeId: ProductTypeId });
    }
  }, [IsOpen]);
  React.useEffect(() => {
    getYears();
  }, []);
  let timeOutGetList: any = null;
  React.useEffect(() => {
    if (prevState.SearchString !== FilterQuery.SearchString) {
      if (timeOutGetList) {
        clearTimeout(timeOutGetList);
        timeOutGetList = null;
      }
      timeOutGetList = setTimeout(() => {
        getListData();
        timeOutGetList = null;
      }, 500);
      setPrevState(FilterQuery);
      return () => window.clearTimeout(timeOutGetList);
    } else {
      getListData();
      setPrevState(FilterQuery);
    }
  }, [FilterQuery]);
  const getYears = async () => {
    if (listYears.length > 0) return;
    let years: any[] = [];
    commonfn.getListYears().forEach((element) => {
      years.push({ key: element, text: element.toString() });
    });
    setListYears(years);
  };
  const getAllBrands = async () => {
    let data = (
      await BrandsApi.apiBrandsPagedPost({
        PageNumber: 1,
        OrderBy: [{ ColumnName: 'Name' }],
        PageSize: 0,
        ProductTypeId: ProductTypeId,
        SearchString: '',
        GetOnlyActive: true,
      })
    ).data;
    let brands: any[] = [{ key: 0, text: t('AllBrands') }];
    data.Data?.forEach((element) => {
      brands.push({ key: element.Id, text: element.Name });
    });
    setListBrand(brands);
  };
  const getAllCategories = async () => {
    let data = (
      await CategoriesApi.apiCategoriesPagedPost({
        PageNumber: 1,
        OrderBy: [{ ColumnName: 'Name' }],
        PageSize: 0,
        ProductTypeId: ProductTypeId,
        SearchString: '',
        GetOnlyActive: true,
      })
    ).data;
    let categories: any[] = [{ key: 0, text: t('AllCategories') }];
    data.Data?.forEach((element) => {
      categories.push({ key: element.Id, text: element.Name });
    });
    setListCategories(categories);
  };
  const getAllVendors = async () => {
    let data = (
      await VendorsApi.apiVendorsPagedPost({
        PageNumber: 1,
        OrderBy: [{ ColumnName: 'Name' }],
        PageSize: 0,
        SearchString: '',
        GetOnlyActive: true,
        ProductTypeId: ProductTypeId,
      })
    ).data;
    let vendors: any[] = [{ key: 0, text: t('AllVendors') }];
    data.Data?.forEach((element) => {
      vendors.push({ key: element.Id, text: element.Name });
    });
    setListVendors(vendors);
  };
  const getModules = async () => {
    let data = (await MatrixApi.apiMatrixsModuleByProductTypeProductTypeIdGet(ProductTypeId)).data;
    setListModules((data.Data as (Api.Module & { DeadLine: string; datePick: boolean })[]) || []);
  };
  const getTitle = (): string => {
    return t('AddProducts') + ' (' + (isModule ? 2 : 1) + ' of 2)';
  };
  const onClose = () => {
    onCloseModal();
    SetIsShow(false);
    SetIsModule(false);
  };
  const validateFormData = () => {
    let count = 0;
    if (listModules) {
      listModules.forEach((item) => {
        if ((item as any).checked) {
          count++;
        }
      });
    }
    return count > 0;
  };
  const onNext = () => {
    let count = 0;
    if (listData) {
      listData.forEach((item) => {
        if ((item as any).checked) {
          count++;
        }
      });
      if (count > 0) {
        SetIsModule(true);
      } else {
        SetShowProductError(true);
        return;
      }
    }
  };
  const onSave = async () => {
    if (!validateFormData()) {
      SetShowModuleError(true);
      return;
    }
    let objPost: AddProductToMatrixCommand = {};
    if (listData) {
      const tmp_data: number[] = [];
      listData.forEach((item) => {
        if ((item as any).checked && item.Id) {
          tmp_data.push(item.Id);
        }
      });
      objPost.ProductIds = tmp_data;
    }
    if (listModules) {
      const tmp_data: Api.ModuleIdAndDeadline[] = [];
      listModules.forEach((item) => {
        if ((item as any).checked && item.Id) {
          tmp_data.push({
            ModuleId: item.Id,
            Deadline:
              item.datePick || item.DefaultDeadlineDay
                ? Moment().add(item.DefaultDeadlineDay, 'd').toDate().toISOString()
                : null,
          });
        }
      });
      objPost.ChooseModules = tmp_data;
    }
    objPost.MatrixYear = Year;
    objPost.ProductTypeId = ProductTypeId;
    MatrixApi.apiMatrixsPost(objPost).then(() => {
      if (onSaveClick) {
        onSaveClick();
      }
    });
    onCloseModal();
    SetIsShow(false);
  };
  const getListData = async () => {
    let data = (await MatrixApi.apiMatrixsProductPagedPost(FilterQuery)).data;
    setListData(data.Data || []);
  };
  return (
    <Modal isOpen={isShow} isModeless={true}>
      <ModalHeader
        title={getTitle()}
        onCloseModal={() => {
          onClose();
        }}
      />
      <div className={'modal-content'} style={{ width: 800 }}>
        {!isModule ? (
          <div>
            <div className="ms-Grid form-list" dir="ltr">
              <div className="ms-Grid-row flex-center">
                <div className="ms-Grid-col padding-left-0" style={{ width: 200 }}>
                  <Dropdown
                    options={listBrand}
                    selectedKey={FilterQuery.BrandId}
                    onChange={(e, opt, index) => {
                      setFilterQuery({ ...FilterQuery, BrandId: parseInt((opt?.key as string) || '0') });
                    }}
                  ></Dropdown>
                </div>
                <div className="ms-Grid-col padding-left-0" style={{ width: 200 }}>
                  <Dropdown
                    options={listCategories}
                    selectedKey={FilterQuery.CategoryId}
                    onChange={(e, opt, index) => {
                      setFilterQuery({ ...FilterQuery, CategoryId: parseInt((opt?.key as string) || '0') });
                    }}
                  ></Dropdown>
                </div>
                <div className="ms-Grid-col padding-left-0" style={{ width: 200 }}>
                  <Dropdown
                    options={listVendors}
                    selectedKey={FilterQuery.VendorId}
                    onChange={(e, opt, index) => {
                      setFilterQuery({ ...FilterQuery, VendorId: parseInt((opt?.key as string) || '0') });
                    }}
                  ></Dropdown>
                </div>
                <div className="ms-Grid-col padding-left-0" style={{ width: 200 }}>
                  <Dropdown
                    options={listYears}
                    selectedKey={FilterQuery.Year}
                    onChange={(e, opt, index) => {
                      setFilterQuery({ ...FilterQuery, Year: parseInt((opt?.key as string) || '2021') });
                    }}
                  ></Dropdown>
                </div>
              </div>
              <div className="ms-Grid-row flex-center">
                <div className="ms-Grid-col padding-left-0" style={{ minWidth: 400 }}>
                  <SearchBox
                    placeholder={t('Search')}
                    onChange={(e, newValue) => {
                      setFilterQuery({ ...FilterQuery, SearchString: newValue || '' });
                    }}
                  />
                </div>
                <div className="ms-Grid-col padding-left-0 display-flex flex-center" style={{ minWidth: 200 }}>
                  <Checkbox
                    label={t('NewProductsOnly')}
                    checked={FilterQuery.IsNewProductonly}
                    onChange={(e, checked) => {
                      setFilterQuery({ ...FilterQuery, IsNewProductonly: checked });
                    }}
                  ></Checkbox>
                  <TooltipHost
                    content="Products that have never added to matrix"
                    id={'hint'}
                    calloutProps={{ gapSpace: 0 }}
                  >
                    <IconButton iconProps={{ iconName: 'Info' }} title="Info" ariaLabel="Info" />
                  </TooltipHost>
                </div>
              </div>
            </div>
            {showProductError && (
              <MessageBar messageBarType={MessageBarType.error} isMultiline={false} dismissButtonAriaLabel="Close">
                {t('PleaseSelectProduct')}
              </MessageBar>
            )}
            <div className="ms-Grid-row">
              {(listData?.length || 0) > 0 && (
                // <DetailsList
                //     items={listData || []}
                //     columns={_columns}
                //     // checkboxVisibility={CheckboxVisibility.hidden}
                // />
                <TeuDetailsList
                  props={{
                    styles: { root: { minHeight: 600 } },
                    items: listData || [],
                    columns: _columns,
                    checkboxVisibility: CheckboxVisibility.always,
                  }}
                />
              )}
              <NoDataFound isShow={listData?.length === 0}></NoDataFound>
            </div>
          </div>
        ) : (
          ''
        )}
        {isModule ? (
          <div className="ms-Grid-row">
            {showModuleError && (
              <MessageBar messageBarType={MessageBarType.error} isMultiline={false} dismissButtonAriaLabel="Close">
                {t('PleaseSelectModule')}
              </MessageBar>
            )}
            <TeuDetailsList
              props={{
                styles: { root: { minHeight: 600 } },
                items: listModules || [],
                columns: _columnsModule,
                compact: true,
                checkboxVisibility: CheckboxVisibility.always,
              }}
            />
            <NoDataFound isShow={(listModules?.length || 0) === 0}></NoDataFound>
          </div>
        ) : (
          ''
        )}
      </div>
      <div className={'modal-footer'}>
        <DefaultButton
          className={'margin-left-10'}
          onClick={() => {
            onClose();
          }}
        >
          {t('Close')}
        </DefaultButton>
        {!isModule ? (
          <PrimaryButton
            className={'margin-left-10'}
            onClick={() => {
              onNext();
            }}
          >
            {t('Add')}
          </PrimaryButton>
        ) : (
          ''
        )}
        {isModule ? (
          <PrimaryButton
            className={'margin-left-10'}
            onClick={() => {
              onSave();
            }}
          >
            {t('Finish')}
          </PrimaryButton>
        ) : (
          ''
        )}
      </div>
    </Modal>
  );
};
export default ModalAddProducts;
